import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    TextField,
    CircularProgress,
} from '@mui/material';
import { generateTwoFactorQrCode, verifyTwoFactor } from '@openapi/auth/auth';
import { useTranslation } from 'react-i18next';
import CommonButton from '../components/atom/CommonButton';
import { QRCodeCanvas } from 'qrcode.react';
import useNotify from '../hooks/useNotify';
import { useAuth } from '../hooks/useAuth';

const EnableTwoFactorModal = ({ showModal, MODAL_TYPE, refetch }) => {
    const [qrCode, setQrCode] = useState(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const { notifySuccess, notifyError } = useNotify();
    const { user } = useAuth();

    const handleClose = () => {
        showModal(MODAL_TYPE.NONE);
    };

    const handleEnable = async () => {
        setIsLoading(true);
        try {
            await verifyTwoFactor({
                token: verificationCode,
                tempToken: user.token,
            });
            notifySuccess(t('users.twoFactorEnabled'));
            await refetch();
            handleClose();
        } catch (error) {
            console.error('Error verifying 2FA code:', error);
            notifyError(t('users.invalidTwoFactorCode'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        async function fetchQrCode() {
            try {
                const qrData = await generateTwoFactorQrCode();
                setQrCode(qrData);
            } catch (error) {
                console.error('Error generating QR code:', error);
                notifyError(error);
            }
        }

        fetchQrCode();
    }, []);

    return (
        <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>{t('users.enableTwoFactor')}</DialogTitle>
            <DialogContent>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('users.enableTwoFactorPlaceholderDescription')}
                    </Typography>
                    <Box
                        sx={{
                            width: 200,
                            height: 200,
                            bgcolor: 'grey.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            mb: 3,
                            border: '2px dashed',
                            borderColor: 'grey.400',
                        }}
                    >
                        {qrCode?.qrcodeuri ? (
                            <QRCodeCanvas value={qrCode.qrcodeuri} size={180} />
                        ) : (
                            <CircularProgress />
                        )}
                    </Box>

                    <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}>
                        {t('users.enterTwoFactorCode')}
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="000000"
                        variant="outlined"
                        value={verificationCode}
                        onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val.length <= 6) setVerificationCode(val);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && verificationCode.length === 6) {
                                handleEnable();
                            }
                        }}
                        slotProps={{
                            htmlInput: {
                                maxLength: 6,
                                style: {
                                    textAlign: 'center',
                                    fontSize: '1.5rem',
                                    letterSpacing: '0.5rem',
                                },
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <CommonButton
                    label={t('common.cancel')}
                    buttonType="secondary"
                    onClick={handleClose}
                />
                <CommonButton
                    label={t('common.apply')}
                    buttonType="primary"
                    onClick={handleEnable}
                    disabled={isLoading || verificationCode.length !== 6}
                />
            </DialogActions>
        </Dialog>
    );
};

export default EnableTwoFactorModal;
